// ============================================================
// A BODY'S NATURES, DRAWN (plan body-map T2; engine/plate/natures).
// Incorporeal's bright outline round a faint body; Phase's shells; a
// hoverer's or flyer's lift; the ink and shadow of a nightmare and its
// shadow that moves wrong (each part on the clock, seeded); Warp
// Instability's backtrack on a hit; Undying's pulse is the wounds bar's
// (./BodyPlate). Simple loops are tracks or CSS keyframes, out of step by
// their offsets; the wrong shadow's turns are a scene the clock writes.
// ============================================================

import { OutlineFilter } from './Outline';
import type { CSSProperties } from 'react';
import {
  BACKTRACK,
  liftLook,
  NIGHT_SHADOWS,
  PHASE_SHELLS,
  WRONG_PARTS,
  wrongAngle,
  wrongPivot,
} from '../../../../engine/plate/natures';
import { firstLanded, landed } from '../../../../engine/plate/burst';
import { anchorOf, type Box, type PlateFrame } from '../../../../engine/plate/frame';
import tx from '../../../../styles/PlateTraits.module.css';
import { tracked } from '../../usePlateClock';
import type { HitPlay } from '../../usePlateHits';
import type { WrittenNode } from '../scene';
import { timed } from '../timing';

/** The natures' own filters: Incorporeal's glowing edge, and the soft blur of shadows and lift. */
export function NatureDefs({ u, incorporeal }: { u: string; incorporeal: boolean }) {
  return (
    <>
      <filter id={`${u}-tblur`}>
        <feGaussianBlur stdDeviation={22} />
      </filter>
      {incorporeal && (
        <OutlineFilter id={`${u}-edge`} radius={9} flood={tx.spectralFlood}>
          <feGaussianBlur in="o" stdDeviation={6} result="g" />
          <feMerge>
            <feMergeNode in="g" />
            <feMergeNode in="o" />
          </feMerge>
        </OutlineFilter>
      )}
    </>
  );
}

/** Incorporeal: the outline round its faint body, pulsing (the body itself thinned on the figure: ./TraitLayers). */
export function IncorporealFront({ u }: { u: string }) {
  return (
    <g
      pointerEvents="none"
      data-trait="incorporeal"
      filter={`url(#${u}-edge)`}
      {...tracked([{ attr: 'opacity', values: [0.85, 1, 0.85], dur: 3.4 }])}
    >
      <use href={`#${u}-sil`} className={tx.silFill} />
    </g>
  );
}

/** Phase: its other positions - four shells breathing out from it and back, each on its own beat. */
export function PhaseBack({ u }: { u: string }) {
  return (
    // One glow over the four shells, not one each: a blur over the whole figure per copy, every frame.
    <g pointerEvents="none" data-trait="phase" filter={`url(#${u}-tglow)`}>
      {PHASE_SHELLS.map((s, k) => (
        <g
          key={k}
          className={tx.phaseShell}
          style={
            {
              '--dx': `${s.dx}px`,
              '--dy': `${s.dy}px`,
              '--op': s.opacity,
              animationDuration: `${s.dur}s`,
              animationDelay: `-${s.offset}s`,
            } as CSSProperties
          }
        >
          <use href={`#${u}-art`} />
        </g>
      ))}
    </g>
  );
}

/** A hoverer's or a flyer's lift under it: its shadow, and the rings and haze, or the downdraft. */
export function LiftBack({ u, kind, box }: { u: string; kind: 'hover' | 'fly'; box: Box }) {
  const look = liftLook(box, kind);
  const { shadow } = look;
  return (
    <g pointerEvents="none" data-trait="lift" data-lift={kind}>
      <ellipse
        className={tx.liftShadow}
        cx={shadow.cx}
        cy={shadow.cy}
        rx={shadow.rx}
        ry={shadow.ry}
        opacity={shadow.opacity}
        filter={`url(#${u}-tblur)`}
      />
      {shadow.dashed && <ellipse className={tx.flyRing} cx={shadow.cx} cy={shadow.cy} rx={shadow.rx} ry={shadow.ry} />}
      {look.rings.map((r, k) => (
        <ellipse
          key={k}
          className={tx.hoverRing}
          cx={look.cx}
          cy={look.gy}
          filter={`url(#${u}-tglow)`}
          {...tracked([r.rx, r.ry, r.opacity])}
        />
      ))}
      {look.haze && (
        <rect
          className={tx.hoverHaze}
          x={look.haze.x}
          y={look.haze.y}
          width={look.haze.width}
          height={look.haze.height}
          filter={`url(#${u}-tblur)`}
          {...tracked([look.haze.opacity])}
        />
      )}
      {look.streaks.map((s, k) => (
        <line key={k} className={tx.flyStreak} x1={s.x} x2={s.x} {...tracked([s.y1, s.y2, s.opacity])} />
      ))}
    </g>
  );
}

/**
 * Stuff of Nightmares, round the figure: a black that gathers about it, breathing. It is gone again before the
 * frame's edge (half its width from the middle, 0.8 of this radius): the plate has no box (plan plate-unboxed), and
 * a black reaching the frame's edge drew one round the figure.
 */
export function NightVignette({ u, frame }: { u: string; frame: { x: number; y: number; w: number; h: number } }) {
  return (
    <g pointerEvents="none" data-trait="nightmares">
      <radialGradient id={`${u}-ng`} cx="50%" cy="45%" r="62%">
        <stop className={tx.nightStop} offset={0.32} stopOpacity={0} />
        <stop className={tx.nightStop} offset={0.6} stopOpacity={0.85} />
        <stop className={tx.nightStop} offset={0.8} stopOpacity={0} />
      </radialGradient>
      <rect
        x={frame.x}
        y={frame.y}
        width={frame.w}
        height={frame.h}
        fill={`url(#${u}-ng)`}
        {...tracked([{ attr: 'opacity', values: [0.85, 1, 0.85], dur: 6 }])}
      />
    </g>
  );
}

/** Its shadow that moves wrong: head and arms in black, each turning on its own about its joint. */
function WrongShadow({ u, parts }: { u: string; parts: Partial<Record<string, Box>> }) {
  return (
    <g className={tx.wrongDrift}>
      {WRONG_PARTS.filter(({ part }) => parts[part]).map(({ part }) => {
        const [px, py] = wrongPivot(part, parts[part]!);
        return (
          <g key={part} data-g={`ns-${part}`} transform={`rotate(0 ${px} ${py})`}>
            <g
              className={tx.nightCopy}
              clipPath={`url(#${u}-c-${part})`}
              filter={`url(#${u}-tblur)`}
              style={{ strokeWidth: part === 'head' ? 130 : 110 }}
            >
              <use href={`#${u}-art`} />
            </g>
          </g>
        );
      })}
    </g>
  );
}

/** Behind the figure: dense black copies of it drifting, and its shadow that moves wrong. */
export function NightBack({ u, parts }: { u: string; parts: Partial<Record<string, Box>> }) {
  return (
    <g pointerEvents="none">
      {/* One blur over the four drifting copies, not one each. */}
      <g filter={`url(#${u}-tblur)`}>
        {NIGHT_SHADOWS.map((s, k) => (
          <g
            key={k}
            className={tx.nightShadow}
            style={{ '--dx': `${s.dx}px`, '--dy': `${s.dy}px`, animationDuration: `${s.dur}s` } as CSSProperties}
          >
            <use href={`#${u}-art`} />
          </g>
        ))}
      </g>
      <WrongShadow u={u} parts={parts} />
    </g>
  );
}

/** What the clock writes of the wrong shadow at `t`: each part's own turn about its joint. */
export function nightNodes(parts: Partial<Record<string, Box>>, t: number): WrittenNode[] {
  return WRONG_PARTS.filter(({ part }) => parts[part]).map(({ part, max }) => {
    const [px, py] = wrongPivot(part, parts[part]!);
    return { key: `ns-${part}`, attrs: { transform: `rotate(${wrongAngle(part, max, t).toFixed(2)} ${px} ${py})` } };
  });
}

/** The wrong shadow at rest: every part where it belongs. */
export const nightStill = (parts: Partial<Record<string, Box>>) =>
  nightNodes(parts, 0).map((n) => ({ ...n, attrs: { transform: '' } }));

/** Warp Instability's backtrack, once an attack lands: its shells flung out, hanging, rewound into it with streaks. */
export function Backtrack({ u, play, box }: { u: string; play: HitPlay; box: Box }) {
  const first = firstLanded(play.burst);
  if (!first) return null;
  const at = first.at + BACKTRACK.delay;
  const [cx, cy] = [(box[0] + box[2]) / 2, box[1] + (box[3] - box[1]) * 0.4];
  return (
    <g key={play.id} pointerEvents="none" data-moment="backtrack">
      {BACKTRACK.shells.map((s, k) => (
        <g
          key={k}
          className={tx.backtrackShell}
          style={timed(at, 0, { '--dx': `${s.dx}px`, '--dy': `${s.dy}px`, '--op': s.opacity })}
        >
          <use href={`#${u}-art`} />
        </g>
      ))}
      {BACKTRACK.shells.map((s, k) => {
        const [ex, ey] = [cx + s.dx * 1.05, cy + s.dy * 1.05];
        // pathLength 1: the streak draws itself from where the shell hung back to the body, then fades.
        return (
          <line
            key={`s${k}`}
            className={tx.backtrackStreak}
            x1={ex}
            y1={ey}
            x2={cx}
            y2={cy}
            pathLength={1}
            style={timed(at + 0.56)}
          />
        );
      })}
    </g>
  );
}

/**
 * Stuff of Nightmares: every hit seals over with ink (the bench's; plan design-gaps, P6) - a blot swelling over each
 * place a hit landed, in its turn, then sinking away.
 */
export function InkSeals({ play, frame }: { play: HitPlay; frame: Pick<PlateFrame, 'anchors' | 'box'> }) {
  return (
    <g pointerEvents="none" data-trait="ink-seals">
      {/* A hit the field blocked plays its block, nothing else: it is never sealed (the Track P simplify). */}
      {landed(play.burst.hits).map((h, i) => {
        const [x, y] = anchorOf(frame, h.location);
        return <circle key={i} className={tx.inkSeal} cx={x} cy={y} r={INK_R} style={timed(h.at)} />;
      })}
    </g>
  );
}

/** An ink seal's radius, in the figure's frame. */
const INK_R = 110;
